import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  createArchitectIntakeSession,
  pingArchitectIntakeSession,
  streamArchitectIntakeMessage,
} from "../api/services/architectIntakeAgentService";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";
import type { ArchitectChatMessage } from "./useArchitectAgentChat";

export function useArchitectIntakeAgentChat() {
  const { activeTenantId } = useAuth();
  const [messages, setMessages] = useState<ArchitectChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("architect-intake", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setContextUsage(null);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!activeTenantId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }

      const stored = getStoredAgentSession("architect-intake", activeTenantId);
      if (!stored?.sessionId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }

      try {
        const valid = await pingArchitectIntakeSession(stored.sessionId);
        if (cancelled) return;
        if (valid) {
          setSessionId(stored.sessionId);
        } else {
          clearStoredAgentSession("architect-intake", activeTenantId);
          setSessionId(null);
        }
      } catch {
        if (!cancelled) {
          clearStoredAgentSession("architect-intake", activeTenantId);
          setSessionId(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }

    setSessionReady(false);
    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    const stored = getStoredAgentSession("architect-intake", activeTenantId);
    if (stored?.sessionId) {
      const valid = await pingArchitectIntakeSession(stored.sessionId);
      if (valid) {
        setSessionId(stored.sessionId);
        return stored.sessionId;
      }
      clearStoredAgentSession("architect-intake", activeTenantId);
    }

    const sid = await createArchitectIntakeSession();
    setSessionId(sid);
    if (activeTenantId) {
      setStoredAgentSession("architect-intake", activeTenantId, sid);
    }
    return sid;
  }, [sessionId, activeTenantId]);

  const resetConversation = useCallback(() => {
    if (activeTenantId) {
      clearStoredAgentSession("architect-intake", activeTenantId);
    }
    setSessionId(null);
    setMessages([]);
    setError(null);
    setContextUsage(null);
    setSessionReady(true);
  }, [activeTenantId]);

  const sendMessage = useCallback(
    async (
      text: string,
      options?: { displayContent?: string },
    ): Promise<string | null> => {
      const trimmed = text.trim();
      if (!trimmed || loading || !sessionReady) return null;

      const userMessage: ArchitectChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        displayContent: options?.displayContent,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMessage: ArchitectChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setLoading(true);
      setError(null);

      try {
        const sid = await ensureSession();
        const result = await streamArchitectIntakeMessage(sid, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + delta } : m,
              ),
            );
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: result.response } : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
        return result.response;
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionReady, ensureSession],
  );

  return {
    messages,
    sendMessage,
    resetConversation,
    loading,
    error,
    sessionId,
    sessionReady,
    contextUsage,
  };
}
